// src/components/game/CodeQuiz.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useScore } from '../../context/ScoreContext';

const CodeQuiz = ({ isDarkMode }) => {
  const { updateScore, getScore } = useScore();
  const [bestScore, setBestScore] = useState(0);

  const questions = useMemo(() => [
    {
      question: 'რას აბრუნებს typeof null?',
      options: ['null', 'undefined', 'object', 'number'],
      correct: 2,
      explanation: 'typeof null === "object" - JavaScript-ის ცნობილი bug!',
      difficulty: 'medium',
      category: 'JavaScript'
    },
    {
      question: 'რომელია React-ის hook-ი?',
      options: ['useState', 'getState', 'setState', 'stateHook'],
      correct: 0,
      explanation: 'useState არის React-ის ძირითადი hook',
      difficulty: 'easy',
      category: 'React'
    },
    {
      question: 'რას ნიშნავს CSS-ში "flex" ?',
      options: ['Flexible Box', 'Fixed Box', 'Float Box', 'Frame Box'],
      correct: 0,
      explanation: 'Flex = Flexible Box Layout',
      difficulty: 'easy',
      category: 'CSS'
    },
    {
      question: 'რომელია JavaScript-ის მონაცემთა ტიპი?',
      options: ['String', 'Integer', 'Float', 'Char'],
      correct: 0,
      explanation: 'String არის JavaScript-ის პრიმიტიული ტიპი',
      difficulty: 'easy',
      category: 'JavaScript'
    },
    {
      question: 'რას აკეთებს "git commit"?',
      options: ['ცვლილებების შენახვა', 'კოდის გაგზავნა', 'კოდის გადმოწერა', 'კოდის წაშლა'],
      correct: 0,
      explanation: 'git commit ინახავს ცვლილებებს ლოკალურად',
      difficulty: 'easy',
      category: 'Git'
    },
    {
      question: 'რას აბრუნებს 2 + "2" JavaScript-ში?',
      options: ['4', '"22"', '22', 'Error'],
      correct: 1,
      explanation: 'Number + String => String (კონკატენაცია)',
      difficulty: 'medium',
      category: 'JavaScript'
    },
    {
      question: 'რომელია CSS-ის position მნიშვნელობა?',
      options: ['absolute', 'relative', 'fixed', 'ყველა ზემოთჩამოთვლილი'],
      correct: 3,
      explanation: 'absolute, relative, fixed - ყველა CSS position-ია',
      difficulty: 'medium',
      category: 'CSS'
    },
    {
      question: 'რას აკეთებს "useEffect" React-ში?',
      options: ['გვერდის რენდერი', 'side effects-ის მართვა', 'state-ის მართვა', 'props-ის მიღება'],
      correct: 1,
      explanation: 'useEffect მართავს side effects-ს (API calls, subscriptions)',
      difficulty: 'medium',
      category: 'React'
    },
    {
      question: '=== და == განსხვავება?',
      options: ['არ არის განსხვავება', '=== ამოწმებს ტიპსაც', '== ამოწმებს ტიპსაც', '=== უფრო სწრაფია'],
      correct: 1,
      explanation: '=== (strict equality) ამოწმებს ტიპსა და მნიშვნელობას',
      difficulty: 'medium',
      category: 'JavaScript'
    },
    {
      question: 'რომელია HTML5-ის ახალი ელემენტი?',
      options: ['<div>', '<span>', '<article>', '<table>'],
      correct: 2,
      explanation: '<article> არის HTML5-ის სემანტიკური ელემენტი',
      difficulty: 'easy',
      category: 'HTML'
    }
  ], []);

  const [current, setCurrent] = useState(0);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(() => {
    const saved = localStorage.getItem('codeQuizBestStreak');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const saved = getScore('code-quiz');
    setBestScore(saved || 0);
  }, [getScore]);

  useEffect(() => {
    const shuffled = [...questions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setShuffledQuestions(shuffled);
  }, [questions]);

  useEffect(() => {
    let timer;
    if (isPlaying && !showResult) {
      timer = setInterval(() => setTime(t => t + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying, showResult]);

  const getFilteredQuestions = () => {
    if (selectedCategory === 'all') return shuffledQuestions;
    return shuffledQuestions.filter(q => q.category === selectedCategory);
  };

  const categories = ['all', ...new Set(questions.map(q => q.category))];

  const handleAnswer = (index) => {
    if (answered) return;
    setSelected(index);
    setAnswered(true);
    setShowExplanation(true);
    setIsPlaying(true);

    const isCorrect = index === shuffledQuestions[current]?.correct;
    if (isCorrect) {
      setScore(prev => prev + 1);
      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak > bestStreak) {
          setBestStreak(newStreak);
          localStorage.setItem('codeQuizBestStreak', String(newStreak));
        }
        return newStreak;
      });
    } else {
      setStreak(0);
    }

    setTimeout(() => {
      if (current < shuffledQuestions.length - 1) {
        setCurrent(prev => prev + 1);
        setSelected(null);
        setAnswered(false);
        setShowExplanation(false);
      } else {
        setShowResult(true);
        setIsPlaying(false);
        const finalScore = score + (isCorrect ? 1 : 0);
        if (finalScore > bestScore) {
          updateScore('code-quiz', finalScore);
          setBestScore(getScore('code-quiz'));
        } else {
          updateScore('code-quiz', finalScore);
          setBestScore(getScore('code-quiz'));
        }
      }
    }, 3500);
  };

  const restart = () => {
    const shuffled = [...questions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setShuffledQuestions(shuffled);
    setCurrent(0);
    setScore(0);
    setShowResult(false);
    setSelected(null);
    setAnswered(false);
    setShowExplanation(false);
    setTime(0);
    setIsPlaying(false);
    setStreak(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getStreakEmoji = () => {
    if (streak >= 5) return '🔥';
    if (streak >= 3) return '⚡';
    if (streak >= 2) return '💪';
    return '📈';
  };

  const getDifficultyEmoji = (diff) => {
    const map = { easy: '🟢', medium: '🟡', hard: '🔴' };
    return map[diff] || '';
  };

  const pageStyle = {
    minHeight: '100vh',
    background: isDarkMode 
      ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)' 
      : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    padding: 'clamp(12px, 3vw, 20px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const containerStyle = {
    maxWidth: 'min(650px, 95vw)',
    width: '100%',
    background: isDarkMode 
      ? 'rgba(26, 26, 46, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    padding: 'clamp(15px, 4vw, 30px)',
    borderRadius: 'clamp(16px, 2vw, 20px)',
    backdropFilter: 'blur(20px)',
    boxShadow: isDarkMode 
      ? '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)' 
      : '0 20px 60px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
    animation: 'slideUp 0.6s ease-out'
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          .game-title {
            background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-size: 200% auto;
            animation: shimmer 3s linear infinite;
          }
          .streak-fire {
            animation: pulse 0.5s infinite;
          }
        `}
      </style>

      <div style={containerStyle}>
        <Link to="/games" style={{
          display: 'inline-block',
          padding: 'clamp(6px, 1vw, 8px) clamp(16px, 2vw, 20px)',
          marginBottom: 'clamp(15px, 2vw, 20px)',
          background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          color: isDarkMode ? '#fff' : '#000',
          textDecoration: 'none',
          borderRadius: 'clamp(10px, 1.5vw, 12px)',
          transition: 'all 0.3s',
          backdropFilter: 'blur(10px)',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
          fontSize: 'clamp(13px, 1.5vw, 15px)'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateX(-5px)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateX(0)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
        }}>
          ← უკან
        </Link>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 'clamp(12px, 1.5vw, 15px)',
          flexWrap: 'wrap',
          gap: 'clamp(8px, 1vw, 10px)'
        }}>
          <h2 style={{ margin: 0, fontSize: 'clamp(18px, 3vw, 28px)' }}>
            <span className="game-title">💻 Code Quiz</span>
          </h2>
          <div style={{ display: 'flex', gap: 'clamp(10px, 1.5vw, 15px)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: '#4ade80', fontWeight: 'bold', fontSize: 'clamp(13px, 1.5vw, 15px)' }}>
              🏆 {bestScore}
            </span>
            <span style={{ color: isDarkMode ? '#aaa' : '#666', fontSize: 'clamp(13px, 1.5vw, 15px)' }}>
              🎯 {score}
            </span>
          </div>
        </div>

        {!showResult ? (
          <>
            {/* Category Filter - Mobile Friendly */}
            <div style={{
              display: 'flex',
              gap: 'clamp(4px, 0.8vw, 8px)',
              marginBottom: 'clamp(12px, 1.5vw, 15px)',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  style={{
                    padding: 'clamp(3px, 0.6vw, 4px) clamp(10px, 1.5vw, 12px)',
                    borderRadius: '20px',
                    border: '2px solid',
                    borderColor: selectedCategory === cat ? '#4facfe' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                    background: selectedCategory === cat ? '#4facfe' : 'transparent',
                    color: selectedCategory === cat ? '#fff' : (isDarkMode ? '#aaa' : '#666'),
                    fontSize: 'clamp(10px, 1.2vw, 11px)',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    fontWeight: selectedCategory === cat ? 'bold' : 'normal',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  {cat === 'all' ? '📚 ყველა' : cat}
                </button>
              ))}
            </div>

            {/* Stats - Mobile Friendly */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
              gap: 'clamp(6px, 1vw, 8px)',
              marginBottom: 'clamp(15px, 2vw, 20px)',
              padding: 'clamp(10px, 1.5vw, 15px)',
              background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
              borderRadius: 'clamp(10px, 1.5vw, 12px)',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }}>📝</div>
                <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
                  {current + 1}/{shuffledQuestions.length}
                </div>
                <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>კითხვა</div>
              </div>
              <div>
                <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }}>⏱️</div>
                <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
                  {formatTime(time)}
                </div>
                <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>დრო</div>
              </div>
              <div>
                <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }} className={streak >= 2 ? 'streak-fire' : ''}>
                  {getStreakEmoji()}
                </div>
                <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>{streak}</div>
                <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>სტრიქი</div>
              </div>
              <div>
                <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }}>🏆</div>
                <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>{bestStreak}</div>
                <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>საუკეთესო</div>
              </div>
            </div>

            {/* Question Info */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'clamp(8px, 1vw, 10px)'
            }}>
              <span style={{
                fontSize: 'clamp(10px, 1.2vw, 11px)',
                background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                padding: '2px 10px',
                borderRadius: '20px',
                color: isDarkMode ? '#aaa' : '#666'
              }}>
                {shuffledQuestions[current]?.category || 'General'}
              </span>
              <span style={{
                fontSize: 'clamp(10px, 1.2vw, 12px)',
                color: isDarkMode ? '#666' : '#999'
              }}>
                {getDifficultyEmoji(shuffledQuestions[current]?.difficulty || 'easy')}
              </span>
            </div>

            {/* Question */}
            <h3 style={{ 
              color: isDarkMode ? '#fff' : '#000', 
              margin: 'clamp(12px, 1.5vw, 15px) 0 clamp(15px, 2vw, 20px) 0',
              fontSize: 'clamp(16px, 2.5vw, 22px)',
              padding: 'clamp(12px, 2vw, 20px)',
              background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
              borderRadius: 'clamp(10px, 1.5vw, 12px)',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
              minHeight: 'clamp(50px, 6vw, 60px)',
              display: 'flex',
              alignItems: 'center',
              lineHeight: '1.5'
            }}>
              {shuffledQuestions[current]?.question}
            </h3>

            {/* Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(8px, 1vw, 10px)' }}>
              {shuffledQuestions[current]?.options.map((opt, idx) => {
                const isCorrect = idx === shuffledQuestions[current].correct;
                const isSelected = selected === idx;
                let buttonStyle = {
                  padding: 'clamp(12px, 2vw, 18px)',
                  borderRadius: 'clamp(10px, 1.5vw, 12px)',
                  border: '2px solid',
                  background: isDarkMode ? 'rgba(26,26,46,0.8)' : 'rgba(255,255,255,0.8)',
                  color: isDarkMode ? '#fff' : '#000',
                  cursor: answered ? 'default' : 'pointer',
                  fontSize: 'clamp(13px, 1.8vw, 16px)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontWeight: isSelected ? 'bold' : 'normal',
                  position: 'relative',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                };

                if (isSelected) {
                  if (isCorrect) {
                    buttonStyle.borderColor = '#4ade80';
                    buttonStyle.background = 'linear-gradient(135deg, #4ade80, #22d3ee)';
                    buttonStyle.color = '#000';
                    buttonStyle.boxShadow = '0 4px 20px rgba(74,222,128,0.3)';
                  } else {
                    buttonStyle.borderColor = '#f87171';
                    buttonStyle.background = 'linear-gradient(135deg, #f87171, #ef4444)';
                    buttonStyle.color = '#fff';
                    buttonStyle.boxShadow = '0 4px 20px rgba(248,113,113,0.3)';
                  }
                } else if (answered && isCorrect) {
                  buttonStyle.borderColor = '#4ade80';
                  buttonStyle.background = 'rgba(74,222,128,0.1)';
                  buttonStyle.boxShadow = '0 0 30px rgba(74,222,128,0.1)';
                }

                return (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(idx)}
                    style={buttonStyle}
                    disabled={answered}
                    onMouseEnter={(e) => {
                      if (!answered && !isSelected) {
                        e.target.style.transform = 'scale(1.02)';
                        e.target.style.borderColor = '#4facfe';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!answered && !isSelected) {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
                      }
                    }}
                  >
                    <span style={{ 
                      display: 'inline-block',
                      minWidth: '24px',
                      color: isSelected ? 'inherit' : (isDarkMode ? '#666' : '#999'),
                      fontWeight: 'bold'
                    }}>
                      {String.fromCharCode(65 + idx)}.
                    </span>
                    {opt}
                    {isSelected && (
                      <span style={{ marginLeft: '10px' }}>
                        {isCorrect ? ' ✅' : ' ❌'}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Explanation */}
            {showExplanation && (
              <div style={{
                marginTop: 'clamp(15px, 2vw, 20px)',
                padding: 'clamp(12px, 1.5vw, 16px)',
                background: isDarkMode ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.02)',
                borderRadius: 'clamp(10px, 1.5vw, 12px)',
                color: isDarkMode ? '#fff' : '#000',
                fontSize: 'clamp(12px, 1.5vw, 15px)',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                borderLeft: `4px solid ${selected === shuffledQuestions[current]?.correct ? '#4ade80' : '#f87171'}`,
                animation: 'slideUp 0.3s ease-out'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>💡</span>
                  <span style={{ fontWeight: 'bold' }}>
                    {selected === shuffledQuestions[current]?.correct ? '✅ სწორია! ' : '❌ არასწორია! '}
                  </span>
                </div>
                <p style={{ margin: '8px 0 0 0', color: isDarkMode ? '#aaa' : '#666' }}>
                  {shuffledQuestions[current]?.explanation}
                </p>
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 'clamp(48px, 10vw, 80px)', margin: '20px 0' }}>
              {score >= shuffledQuestions.length * 0.8 ? '🎉' : 
               score >= shuffledQuestions.length * 0.5 ? '😊' : '😅'}
            </div>
            <h2 style={{ color: isDarkMode ? '#fff' : '#000', fontSize: 'clamp(24px, 3vw, 32px)' }}>
              🏁 დასრულდა!
            </h2>
            <p style={{ 
              fontSize: 'clamp(28px, 5vw, 40px)', 
              color: isDarkMode ? '#fff' : '#000', 
              fontWeight: 'bold' 
            }}>
              {score}/{shuffledQuestions.length}
            </p>
            <p style={{ 
              color: isDarkMode ? '#aaa' : '#666',
              fontSize: 'clamp(14px, 1.5vw, 18px)'
            }}>
              📊 სიზუსტე: {Math.round((score / shuffledQuestions.length) * 100)}%
            </p>
            <p style={{ 
              color: isDarkMode ? '#aaa' : '#666',
              fontSize: 'clamp(12px, 1.5vw, 14px)'
            }}>
              ⏱️ დრო: {formatTime(time)}
            </p>
            <div style={{
              margin: '15px 0',
              padding: '12px',
              background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
              borderRadius: '10px'
            }}>
              <span style={{ color: '#FFD93D' }}>🏆</span>
              <span style={{ color: isDarkMode ? '#fff' : '#000', marginLeft: '8px' }}>
                საუკეთესო: {bestScore}
              </span>
              {bestStreak > 0 && (
                <span style={{ marginLeft: '15px', color: '#4ade80' }}>
                  🔥 სტრიქი: {bestStreak}
                </span>
              )}
            </div>
            <button onClick={restart} style={{
              padding: 'clamp(12px, 1.5vw, 16px) clamp(30px, 5vw, 50px)',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 'clamp(10px, 1.5vw, 12px)',
              fontSize: 'clamp(14px, 1.5vw, 18px)',
              cursor: 'pointer',
              marginTop: '20px',
              transition: 'all 0.3s',
              fontWeight: 'bold',
              boxShadow: '0 4px 20px rgba(79,172,254,0.3)',
              touchAction: 'manipulation',
              width: '100%',
              maxWidth: '300px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px) scale(1.02)';
              e.target.style.boxShadow = '0 8px 30px rgba(79,172,254,0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 4px 20px rgba(79,172,254,0.3)';
            }}>
              🔄 თავიდან
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeQuiz;